import { useEffect, useRef, useState } from 'react';
import type { PreparePrint } from '../lib/viewerPrint';

/** Each open viewer owns a print-only surface. Browser menu printing and Ctrl+P
 * select only the top viewer; no app/chat markup is copied into that surface. */
export function useViewerPrint(prepare: PreparePrint | null, active = true) {
  const anchor = useRef<HTMLDivElement>(null);
  const action = useRef<(() => Promise<void>) | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!active) return;
    setError('');
    const controller = new AbortController();
    const root = document.createElement('div'); root.className = 'viewer-print-root';
    root.textContent = 'Документ готовится к печати. Закройте предварительный просмотр и нажмите «Печать» в окне документа.';
    document.body.append(root);
    let dispose: (() => void) | undefined;
    const isTop = () => {
      const dialog = anchor.current?.closest('[role="dialog"]');
      return Boolean(dialog && [...document.querySelectorAll('[role="dialog"]')].at(-1) === dialog);
    };
    const ready = prepare?.(controller.signal).then(content => {
      if (controller.signal.aborted) { content.dispose(); return; }
      dispose = content.dispose; root.replaceChildren(content.element);
    }).catch(reason => {
      if (!controller.signal.aborted) {
        const message = `Не удалось подготовить печать: ${reason instanceof Error ? reason.message : 'ошибка чтения файла'}`;
        root.textContent = message; setError(message);
      }
      throw reason;
    });
    // Preparation also covers printing from the browser menu, which cannot
    // await asynchronous rendering inside its beforeprint event.
    void ready?.catch(() => {});
    const before = () => {
      if (!isTop()) return;
      document.body.classList.add('viewer-printing'); root.classList.add('is-printing');
    };
    const after = () => { root.classList.remove('is-printing'); document.body.classList.remove('viewer-printing'); };
    const print = async () => {
      if (!isTop()) return;
      if (!ready) { setError('Дождитесь открытия документа перед печатью.'); return; }
      setBusy(true);
      try {
        await ready;
        if (controller.signal.aborted || !isTop()) return;
        before(); window.print();
      } catch { /* Preparation already reports the error; never print the app. */ }
      finally { if (!controller.signal.aborted) setBusy(false); }
    };
    action.current = print;
    const key = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p' && isTop()) {
        event.preventDefault(); event.stopPropagation(); void print();
      }
    };
    window.addEventListener('beforeprint', before); window.addEventListener('afterprint', after);
    document.addEventListener('keydown', key, true);
    return () => {
      controller.abort(); action.current = null; dispose?.(); after(); root.remove();
      window.removeEventListener('beforeprint', before); window.removeEventListener('afterprint', after);
      document.removeEventListener('keydown', key, true);
    };
  }, [prepare, active]);
  return { anchor, print: () => { void action.current?.(); }, busy, error };
}
